import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { Switch } from "@mui/material";
import { Plus } from "lucide-react";

import { UploadResultsModal } from "components/Modal/UploadResultsModal";
import { Button } from "components/ui/Button";
import { TabSwitcher } from "components/ui/TabSwitcher";
import { useProcessNewMessages } from "features/healthlink/hooks/useProcessNewMessages";
import { OutstandingLabTestsTable } from "features/labOrders/components/OutstandingLabTestsTable/OutstandingLabTestsTable";
import { useOverdueLabTestsCount } from "features/labOrders/hooks/useOverdueLabTestsCount";
import { useAllStaffMembers } from "features/practice/hooks/useAllStaffMembers";
import { usePracticeSettings } from "features/practice/hooks/usePracticeSettings";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { NeedsAttentionTable } from "features/pregnancy/components/needsAttention/NeedsAttentionTable";
import { useUnlinkedMncmsCount } from "features/pregnancy/hooks/useUnlinkedMncmsCount";
import { CommonResultDetails } from "features/results/components/CommonResultDetails/CommonResultDetails";
import { RadiologyResultDetails } from "features/results/components/RadiologyResultDetails/RadiologyResultDetails";
import { ResultDetails } from "features/results/components/ResultDetails/ResultDetails";
import { RESULTS_TAB_MESSAGE_TYPES } from "features/results/constants/messageTypeGroups";
import { StaffResultsTable } from "features/results/global/StaffResultsTable/StaffResultsTable";
import { useUnassignedResultsCount } from "features/results/hooks/useUnassignedResultsCount";
import { useUploadManualResults } from "features/results/hooks/useUploadManualResults";
import { findRelatedResult } from "features/results/utils/resultCorrection";

import { ResultsDetailPanel } from "./components/ResultsDetailPanel/ResultsDetailPanel";
import { ResultsDetailPlaceholder } from "./components/ResultsDetailPlaceholder/ResultsDetailPlaceholder";
import { getTabOptions } from "./components/ResultsNavbar/utils/menuItems";
import { selectedItemToTitle } from "./components/ResultsNavbar/utils/selectedItemToTitle";
import { UnmatchedAssignSection } from "./components/UnmatchedAssignSection/UnmatchedAssignSection";
import styles from "./ResultsPage.module.css";

// Maps a tab to its backend results "type". Laboratory and Radiology use their
// dedicated endpoints; every other tab is backed by the common endpoint and
// further narrowed by `messageTypes` (see RESULTS_TAB_MESSAGE_TYPES).
const STAFF_TABLE_TYPES = {
  laboratory: "laboratory",
  radiology: "radiology",
};

const getStaffTableType = (tab) => STAFF_TABLE_TYPES[tab] ?? "common";

/*
 * Maternity is the one tab not backed by the results endpoints. Inbound MN-CMS
 * messages live in their own table with their own linking rules — a patient AND a
 * pregnancy, where attaching the pregnancy is what releases the outbound send gate —
 * so the tab renders its own worklist rather than the shared table and detail panel.
 * It sits here because an unmatched maternity message is the same problem as an
 * unmatched result, and one place to look was the point.
 */
const MATERNITY_TAB = "maternity";

/*
 * Outstanding lab tests are the same kind of tab for the same kind of reason: a test
 * that never came back is not a result, so it is on none of the results endpoints and
 * has nothing to show in the detail panel. It renders its own worklist, and it sits
 * here because "has everything I ordered come back?" is asked in the same sitting as
 * "what came back?".
 */
const OUTSTANDING_LAB_TESTS_TAB = "outstanding-lab-tests";

// The two tabs that bring their own table and need none of the results chrome — no
// unmatched switch, no manual upload, no "download new results".
const SELF_CONTAINED_TABS = [MATERNITY_TAB, OUTSTANDING_LAB_TESTS_TAB];

const ResultsPage = () => {
  const [selectedItem, setSelectedItem] = useState("all-results");
  const [showUnmatched, setShowUnmatched] = useState(false);

  const { data: unassignedCount } = useUnassignedResultsCount();
  const { data: unlinkedMaternityCount } = useUnlinkedMncmsCount();
  const { data: overdueLabTestsCount } = useOverdueLabTestsCount();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  // The table's current page, kept so the detail can link to a correction's counterpart.
  const [loadedResults, setLoadedResults] = useState([]);

  const { mutate: processNewMessages, isPending } = useProcessNewMessages(() =>
    toast.success("New messages processed successfully!")
  );
  const { mutate: uploadManualResults, isPending: isUploading } =
    useUploadManualResults(() => {
      setIsModalOpen(false);
    });

  const { data: staffMember } = useStaffInfo();
  const { data: allStaffMembers } = useAllStaffMembers();
  const { data: practiceSettings } = usePracticeSettings();
  // HealthLink off → no "Download new results" (process) action.
  const isHealthlinkEnabled = practiceSettings?.healthLink !== false;

  const tenantVal = localStorage.getItem("kinesin-tenant");

  const handleSaveFiles = (files) => {
    uploadManualResults({ files, resultType: selectedItem });
  };

  const handleSelectItem = (item) => {
    setDetailItem(item);
  };

  const handleTabChange = (value) => {
    setSelectedItem(value);
    setDetailItem(null);
  };

  const handleUnmatchedToggle = () => {
    setShowUnmatched((prev) => !prev);
    setDetailItem(null);
  };

  const tabOptions = useMemo(
    () =>
      getTabOptions({
        [MATERNITY_TAB]: unlinkedMaternityCount,
        // Only the overdue ones are counted on the tab. Every outstanding test would
        // be a number that never goes down — a practice always has tests in flight —
        // and the point of the badge is the ones nobody has noticed.
        [OUTSTANDING_LAB_TESTS_TAB]: overdueLabTestsCount,
      }),
    [unlinkedMaternityCount, overdueLabTestsCount]
  );

  const isMaternityTab = selectedItem === MATERNITY_TAB;
  const isOutstandingLabTestsTab = selectedItem === OUTSTANDING_LAB_TESTS_TAB;
  const isSelfContainedTab = SELF_CONTAINED_TABS.includes(selectedItem);

  // Effective tab carries the unmatched suffix so downstream components
  // (detail panel, assign section) keep their existing tab-based logic.
  const effectiveTab = showUnmatched
    ? `${selectedItem}-unmatched`
    : selectedItem;

  const staffTableType = getStaffTableType(selectedItem);
  const messageTypes = RESULTS_TAB_MESSAGE_TYPES[selectedItem];

  const renderTable = () => (
    <StaffResultsTable
      type={staffTableType}
      messageTypes={messageTypes}
      // "Unmatched" switch → unassigned filter (server-side); off = all.
      unassigned={showUnmatched ? true : undefined}
      tenantVal={tenantVal}
      onSelectItem={handleSelectItem}
      selectedItemId={detailItem?.id}
      onResultsLoaded={setLoadedResults}
    />
  );

  /*
   * The result this one corrects, or the correction that replaced it — when it is on the page the
   * table currently holds. Null otherwise, and the notice then states the relationship without
   * offering a link it could not honour.
   */
  const relatedResult = detailItem
    ? findRelatedResult(detailItem, loadedResults)
    : null;

  // Previous and next follow the rows on the table's current page, in the order shown.
  const openIndex = detailItem
    ? loadedResults.findIndex((result) => result.id === detailItem.id)
    : -1;
  const previousResult =
    openIndex > 0 ? loadedResults[openIndex - 1] : undefined;
  const nextResult =
    openIndex !== -1 ? loadedResults[openIndex + 1] : undefined;

  const renderDetailContent = () => {
    if (!detailItem) return null;

    const laboratoryDetails = (
      <ResultDetails
        item={detailItem}
        staffMember={staffMember}
        healthlinkPatient={
          showUnmatched ? detailItem.patientInformationExternal : undefined
        }
      />
    );

    const radiologyDetails = (
      <RadiologyResultDetails
        item={detailItem}
        staffMember={showUnmatched ? staffMember : undefined}
      />
    );

    if (selectedItem === "radiology") {
      return radiologyDetails;
    }

    if (selectedItem === "laboratory") {
      return laboratoryDetails;
    }

    // Common-backed tabs (All Results + the message-type filtered tabs) mix
    // result kinds, so pick the detail view by resultType. Other types render
    // the common detail, which exposes an XSLT-styled HL7 preview tab (with a
    // structured fallback when no stylesheet is available).
    const resultType = (detailItem.resultType || "").toUpperCase();

    if (resultType.includes("LAB")) {
      return laboratoryDetails;
    }
    if (resultType.includes("RAD")) {
      return radiologyDetails;
    }

    return <CommonResultDetails item={detailItem} />;
  };

  return (
    <div className={styles.reportsPageWrapper}>
      <TabSwitcher
        options={tabOptions}
        value={selectedItem}
        onChange={handleTabChange}
        fullWidth
        withOverflow
        marginTop={16}
        marginBottom={24}
      />

      <div className={styles.reportTableWrapper}>
        <div className={styles.reportTableHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <h3>{selectedItemToTitle(selectedItem)}</h3>

            {/* The switch filters the results endpoints, which do not serve this
                tab — its list is unmatched messages by definition. */}
            {!isSelfContainedTab && (
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
                fontSize: "15px",
                fontWeight: 600,
              }}
            >
              <Switch
                checked={showUnmatched}
                onChange={handleUnmatchedToggle}
                sx={{
                  padding: "6px",
                  borderRadius: "16px",
                  "& .MuiSwitch-switchBase": {
                    "&.Mui-checked": {
                      color: "#fff",
                      "& + .MuiSwitch-track": {
                        backgroundColor: "var(--green-800)",
                        opacity: 1,
                      },
                    },
                  },
                  "& .MuiSwitch-thumb": {
                    backgroundColor: "#fff",
                  },
                  "& .MuiSwitch-track": {
                    backgroundColor: "#a0a0a0",
                    opacity: 0.5,
                    padding: 0,
                    borderRadius: "16px",
                  },
                }}
              />
              Unmatched
              {/* The number the switch would reveal, without having to flip it. */}
              {unassignedCount > 0 && (
                <span className={styles.unmatchedCount}>{unassignedCount}</span>
              )}
            </label>
            )}
          </div>

          {/* Neither action applies to inbound maternity messages: they are not
              uploaded by hand, and they arrive on their own batch. */}
          {!isSelfContainedTab && (
            <div style={{ display: "flex", gap: "20px" }}>
              <Button
                variant="text"
                onClick={() => setIsModalOpen(true)}
                disabled={isPending || isUploading}
                endIcon={Plus}
              >
                Add manually
              </Button>

              {isHealthlinkEnabled && (
                <Button
                  onClick={processNewMessages}
                  disabled={isPending || isUploading}
                >
                  Download new results
                </Button>
              )}
            </div>
          )}
        </div>

        {isMaternityTab && <NeedsAttentionTable />}

        {isOutstandingLabTestsTab && <OutstandingLabTestsTable />}

        {!isSelfContainedTab && (
        <div
          className={
            showUnmatched ? styles.splitLayoutWide : styles.splitLayout
          }
        >
          <div className={styles.tableColumn}>{renderTable()}</div>

          <div className={styles.detailColumn}>
            {detailItem ? (
              <ResultsDetailPanel
                item={detailItem}
                selectedTab={effectiveTab}
                staffMembers={allStaffMembers}
                onOpenRelatedResult={
                  relatedResult
                    ? () => handleSelectItem(relatedResult)
                    : undefined
                }
                onPrevious={
                  previousResult
                    ? () => handleSelectItem(previousResult)
                    : undefined
                }
                onNext={
                  nextResult ? () => handleSelectItem(nextResult) : undefined
                }
                onConfirm={() =>
                  setDetailItem((prev) => ({
                    ...prev,
                    orderStatus: "CONFIRMED",
                  }))
                }
                assignSection={
                  // Always offered. A result with no patient needs assigning, and
                  // one with the wrong patient needs correcting — the matching is
                  // done on the message's own demographics and does get it wrong.
                  // The section itself decides which of the two it is showing.
                  <UnmatchedAssignSection
                    item={detailItem}
                    // In the Unmatched view the assigned result drops off the
                    // list, so close its preview too. In the normal list keep
                    // the preview open and just reflect the new patient.
                    onAssigned={(patient) =>
                      showUnmatched
                        ? setDetailItem(null)
                        : setDetailItem((prev) =>
                            prev
                              ? { ...prev, patientId: patient?.id, patient }
                              : prev
                          )
                    }
                  />
                }
              >
                {renderDetailContent()}
              </ResultsDetailPanel>
            ) : (
              <ResultsDetailPlaceholder />
            )}
          </div>
        </div>
        )}
      </div>

      <UploadResultsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveFiles}
        isUploading={isUploading}
      />
    </div>
  );
};

export { ResultsPage };
